import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import uploadPhotoAsync from '@/utilities/uploadPhotoAsync';
import { ADD_CUSTOM_DOCUMENT, DELETE_CUSTOM_DOCUMENT, DOCUMENTS_BY_CLIENT_ID } from '../CustomDocuments/graphql';
import { documentsFor, draftWithFile, groupDocuments, insertPayload, typeOptionsFor } from './utils';

/**
 * Query, mutations, upload and modal state for a document panel.
 *
 * CustomDocuments and ArchiveDocuments had near-identical copies of all of this; the only real
 * differences were which `archived` rows they showed and whether they wrote `archived: true`.
 * Both now come through here, so a fix lands in one place.
 *
 * @param {{ clientId: string, archived: boolean, allowedTypes?: string[] }} args
 */
export const useDocuments = ({ clientId, archived, allowedTypes }) => {
    const [notification, setNotification] = useState({});
    const [draft, setDraft] = useState(null);
    const [uploading, setUploading] = useState(false);

    const variables = useMemo(() => ({ client_id: clientId }), [clientId]);

    const { data, loading: documentsLoading } = useQuery(DOCUMENTS_BY_CLIENT_ID, { variables });

    const documents = useMemo(() => documentsFor(data?.documents, archived), [data, archived]);
    const groups = useMemo(() => groupDocuments(documents), [documents]);
    const typeOptions = useMemo(() => typeOptionsFor({ custom: !archived, allowedTypes }), [archived, allowedTypes]);

    const onError = (message) => (e) => {
        console.error(e);
        captureException(e);
        setNotification({ severity: 'error', message });
    };

    const [addDocument, { loading: addLoading }] = useMutation(ADD_CUSTOM_DOCUMENT, {
        onError: onError('Error uploading document'),
    });

    const [deleteDocument, { loading: deleteLoading }] = useMutation(DELETE_CUSTOM_DOCUMENT, {
        onError: onError('Error deleting document'),
    });

    const handleFileAdd = async (file, fileType) => {
        if (!file) return;
        setUploading(true);
        try {
            const link = await uploadPhotoAsync(URL.createObjectURL(file), 'custom_documents', fileType);
            setDraft((prev) => draftWithFile(prev, { link, fileName: file.name }));
        } catch (error) {
            console.error(error);
            captureException(error);
            setNotification({ severity: 'error', message: 'Error uploading document' });
        }
        setUploading(false);
    };

    const save = async () => {
        await addDocument({
            variables: { object: insertPayload({ clientId, draft, archived }) },
            onCompleted: () => setDraft(null),
            update: (cache, { data: { insert_documents_one } }) => {
                cache.updateQuery({ query: DOCUMENTS_BY_CLIENT_ID, variables }, (prev) => ({
                    documents: [...(prev?.documents || []), insert_documents_one],
                }));
            },
        });
    };

    const remove = async (document_id) => {
        await deleteDocument({
            variables: { document_id },
            update: (cache, { data: { delete_documents_by_pk } }) => {
                cache.updateQuery({ query: DOCUMENTS_BY_CLIENT_ID, variables }, (prev) => ({
                    documents: (prev?.documents || []).filter(
                        (doc) => doc.document_id !== delete_documents_by_pk.document_id
                    ),
                }));
            },
        });
    };

    const loading = addLoading || deleteLoading;

    return {
        state: {
            documents,
            groups,
            typeOptions,
            draft,
            uploading,
            loading,
            documentsLoading,
            // Save needs all three.
            valid: !!(draft?.name && draft?.link && draft?.type),
            notification,
        },
        callbacks: {
            openUpload: () => setDraft({}),
            closeUpload: () => setDraft(null),
            editDraft: (patch) => setDraft((prev) => ({ ...prev, ...patch })),
            handleFileAdd,
            save,
            remove,
            clearNotification: () => setNotification({}),
        },
    };
};
